<template>
  <modal
    class=".table-file-download"
    :visible="isDownloadModalVisible"
    :title="capitalize($t('actions.download'))"
    :body="capitalize($t('messages.chooseAFileFormat'))"
    button1-class="default"
    :button1-caption="capitalize($t('actions.cancel'))"
    :button2-visible="false"
    @confirmed="isDownloadModalVisible = false"
    @cancelled="isDownloadModalVisible = false">
    <template #modal-after-form>
      <div class="flex-v-full">
        <div class="file-types">
          <div class="file-type">
            <a
              class="download-csv-link"
              data-action-type="download-csv-file"
              @click="downloadCSV">
              <IconFileCSV />
            </a>
          </div>

          <div class="file-type">
            <a
              class="download-xlsx-link"
              data-action-type="download-xlsx-file"
              @click="downloadXLSX">
              <IconFileXLS />
            </a>
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts" generic="T">
  import Excel from 'exceljs';
  import { DateTime } from 'luxon';
  import Papa from 'papaparse';
  import { capitalize, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import Modal from '@/common/components/controls/Modal.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { IconFileCSV, IconFileXLS } from '@/common/icons/monochrome';
  import { DASH, toServerDateTime } from '@/globals';
  import { generateCSVURI } from '@/utils/CsvUtils';
  import DownloadUtils from '@/utils/DownloadUtils';

  const i18n = useI18n();

  type TableDownloadColumn = TableColumn & { transform?: (val: string) => string; hyperlink?: string; width?: number };

  interface IProps {
    clientSideFiltering: boolean;
    tableDownloadColumns: TableDownloadColumn[];
    exportFileName: string;
  }

  const { clientSideFiltering, tableDownloadColumns, exportFileName } = defineProps<IProps>();

  interface IEmits {
    (event: 'download', listener: string | ((data: T[]) => void)): void;
  }

  const emits = defineEmits<IEmits>();

  const isDownloadModalVisible = ref(false);
  const lastDownloadedFilename = ref('');

  function downloadCSV() {
    if (clientSideFiltering) {
      emits('download', 'csv');
      isDownloadModalVisible.value = false;
    } else {
      emits('download', (data) => {
        const csvHeader = tableDownloadColumns.map((c) => c.label);
        const csvRows = data.map((d) =>
          tableDownloadColumns.map((c) => {
            let val = c.property instanceof Function ? c.property(d) : d[c.property];
            if (c.transform) {
              val = c.transform(val);
            }
            if (c.tableDownloadColumn) {
              val = d[c.tableDownloadColumn];
            }
            if (c.type === 'date' || c.type === 'datetime') {
              val = val != null && val !== DASH ? DateTime.fromISO(val).toUTC().toISO({ includeOffset: true }) : DASH;
            }
            if (c.type === TableColumnTypes.link) {
              if (val != null && val != DASH) {
                c.hyperlink = val;
              } else {
                val = DASH;
              }
            }
            return val;
          }),
        );
        const csv = Papa.unparse({
          fields: csvHeader,
          data: csvRows,
        });
        const csvContent = generateCSVURI(csv);
        const localTimeInUtc = toServerDateTime(DateTime.local());
        lastDownloadedFilename.value = exportFileName ? `${exportFileName}_${localTimeInUtc}.csv` : 'export.csv';

        const link = document.createElement('a');
        link.setAttribute('href', csvContent);
        link.setAttribute('download', lastDownloadedFilename.value);
        document.body.appendChild(link); // Required for FF

        link.click();

        isDownloadModalVisible.value = false;
      });
    }
  }

  function downloadXLSX() {
    if (clientSideFiltering) {
      emits('download', 'xlsx');
      isDownloadModalVisible.value = false;
    } else {
      emits('download', (data) => {
        //
        // Extract currency string format variables from i18n.
        //
        let addSpaceBetweenNumberAndCurrency = false;
        let isCurrencySymbolLeading = true;
        let currencySymbol = '';

        const currencyString = i18n.n(123456789.0123456, 'currency');
        const numberMatches = currencyString.match(/(?:[^\W]+[0-9\s,.])+/g);
        if (numberMatches.length > 0) {
          addSpaceBetweenNumberAndCurrency = currencyString.indexOf(' ') > -1;
          isCurrencySymbolLeading = !currencyString.startsWith(numberMatches[0]);
          currencySymbol = currencyString.replace(' ', '').replace(numberMatches[0], '');
        }

        //
        // Create the XLSX doc.
        //
        const columnHeaders = tableDownloadColumns.map((c) => c.label);
        const dataRows = data.map((d) =>
          tableDownloadColumns.map((c) => {
            let val = c.property instanceof Function ? c.property(d) : d[c.property];

            if (c.type === 'date' || c.type === 'datetime') {
              val =
                val != null && val !== DASH
                  ? DateTime.fromISO(val, { zone: 'utc' }).toFormat('yyyy-MM-dd hh-mm-ss a')
                  : DASH;
            }
            if (c.transform) {
              val = c.transform(val);
            }
            if (c.tableDownloadColumn) {
              val = d[c.tableDownloadColumn];
            }
            if (c.type === TableColumnTypes.link) {
              val =
                val != null && val != DASH
                  ? {
                      text: val,
                      hyperlink: val,
                    }
                  : DASH;
            }
            return val;
          }),
        );

        const workbook = new Excel.Workbook();

        workbook.views = [
          {
            x: 0,
            y: 0,
            width: 30000,
            height: 20000,
            firstSheet: 0,
            activeTab: 1,
            visibility: 'visible',
          },
        ];

        const sheet = workbook.addWorksheet('Data');

        //
        // Apply formatting based on column type.
        //
        tableDownloadColumns.forEach((col, i) => {
          const sheetColumn = sheet.getColumn(i + 1);
          let baseColumnWidth = 15;
          switch (col.type) {
            case 'boolean': {
              baseColumnWidth = 10;
              break;
            }
            case 'percentage': {
              sheetColumn.style.numFmt = '0.00%';
              break;
            }
            case 'currency': {
              const prefix = `${isCurrencySymbolLeading ? currencySymbol : ''}${
                addSpaceBetweenNumberAndCurrency ? ' ' : ''
              }`;
              const suffix = `${addSpaceBetweenNumberAndCurrency ? ' ' : ''}${
                isCurrencySymbolLeading ? '' : currencySymbol
              }`;
              const decimals = currencySymbol === '¥' ? '' : '.00';
              sheetColumn.style.numFmt = `${prefix}#,0${decimals}${suffix}`;
              break;
            }
            case 'integer': {
              sheetColumn.style.numFmt = '#,0';
              break;
            }
            case 'coordinate': {
              sheetColumn.style.numFmt = '#,0.000000';
              break;
            }
            case 'date':
            case 'datetime': {
              sheetColumn.style.numFmt = 'yyyy-MM-dd hh-mm-ss a';
              baseColumnWidth = 18;
              break;
            }
            case 'link': {
              baseColumnWidth = 90;
              break;
            }
            default:
              break;
          }
          sheetColumn.width = col.width != null ? col.width : baseColumnWidth;
        });

        sheet.addRow(columnHeaders);
        dataRows.forEach((x, idxRow) => {
          sheet.addRow(x);
          x.forEach((cell, idxCell) => {
            if (cell?.hyperlink) {
              sheet.getCell(idxRow + 2, idxCell + 1).value = cell;
            }
          });
        });

        workbook.xlsx.writeBuffer().then((blobData) => {
          const blob = new window.Blob([blobData], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          const localTimeInUtc = toServerDateTime(DateTime.local());
          const fileName = exportFileName ? `${exportFileName}_${localTimeInUtc}.xlsx` : 'export.xlsx';
          DownloadUtils.saveBlobToFile(blob, fileName);
          isDownloadModalVisible.value = false;
        });
      });
    }
  }

  function open() {
    isDownloadModalVisible.value = true;
  }

  defineExpose({ open });
</script>
